import { createSlice } from '@reduxjs/toolkit';

export const videoChatSlice = createSlice({
  name: 'videoChat',
  initialState: {
    meetingSession: null,
    // The thread the live session was joined on. The open chat thread can move
    // underneath a running call - a chat notification click switches it, as does
    // picking a group - and every session-scoped endpoint (get-attendees,
    // start/stop-recording) resolves the session by thread id, so they have to
    // be addressed at this one rather than at whatever is currently open.
    // Written and cleared in lockstep with meetingSession.
    sessionThread: null,
    users: {},
    isRecording: false,
    // Current face-effect selection per participant, keyed by Lixani user id
    // (= Chime ExternalUserId). Each entry is { selection, ts }, where the
    // selection holds at most one { id, color } per category. Written only from
    // its owner's authoritative 'state' broadcasts; `ts` is the broadcast's
    // server timestamp, used to drop out-of-order updates.
    participantEffects: {},
  },
  reducers: {
    setMeetingSession: (state, action) => {
      state.meetingSession = action.payload;
      if (!action.payload) {
        state.sessionThread = null;
      }
    },
    // A page refresh coming back to the seat it held: the session and the
    // thread it was joined on arrive from the tab's snapshot (see
    // utils/callSnapshot.js) rather than from a join.
    restoreMeetingSession: (state, action) => {
      const { meetingSession, thread } = action.payload;
      state.meetingSession = meetingSession;
      state.sessionThread = thread || null;
    },
    setIsRecording: (state, action) => {
      state.isRecording = action.payload;
    },
    setParticipantEffects: (state, action) => {
      const { userId, selection, ts } = action.payload;
      const existing = state.participantEffects[userId];
      if (existing && existing.ts && ts && ts < existing.ts) {
        return;
      }
      state.participantEffects[userId] = { selection, ts };
    },
    removeParticipantEffects: (state, action) => {
      delete state.participantEffects[action.payload];
    },
    resetParticipantEffects: (state) => {
      state.participantEffects = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase('videoChat/joinSession/fulfilled', (state, action) => {
        state.meetingSession = action.payload;
        // Taken from the thunk argument rather than the response, so it is the
        // thread we actually joined even if the open thread changed while the
        // join was in flight.
        state.sessionThread = action.meta?.arg?.thread || null;
      })
      .addCase('videoChat/loadUsers/fulfilled', (state, action) => {
        state.users = { ...state.users, ...action.payload };
      });
  },
});
// ACTIONS
export const {
  setMeetingSession,
  restoreMeetingSession,
  setIsRecording,
  setParticipantEffects,
  removeParticipantEffects,
  resetParticipantEffects,
} = videoChatSlice.actions;
// SELECTORS
export const getVideoChatControlData = (state) => {
  return state.videoChatSlice;
};
// Stable empty object so components using this selector do not re-render for
// participants who have nothing selected.
const EMPTY_SELECTION = {};
export const selectParticipantSelection = (state, userId) =>
  state.videoChatSlice.participantEffects[userId]?.selection || EMPTY_SELECTION;

// The participant's voice entry, or null. Stable by construction — it returns
// the stored object or null, never a fresh one — so the tile badge can read it
// straight from useSelector without memoising.
//
// Works for the local user too: broadcastEffectsState mirrors our own selection
// into this map, which is what lets one selector drive the badge on every tile.
export const selectParticipantVoice = (state, userId) =>
  state.videoChatSlice.participantEffects[userId]?.selection?.voice || null;
